<template>
  <div ref="filterModal" :class="$style.filterModal">
    <div :class="[$style.filterModalControls, {[$style.filterModalControlsSingle]: !isFilterable || !isSortable}]">
      <client-only>
        <lv-button
          v-if="isFilterable"
          styling="small-secondary-dark"
          shrink
          @click="openFilterModal"
        >
          <span :class="$style.filterModalControlsButtonContent">
            {{ $awt('aw.common.product_list.filter.filter_row.filter_with_count', { count: filterCount || '' }) }}
            <lv-icon
              name="filter-16"
              :size="16"
              :inherit-color="true"
              style="vertical-align: unset;"
            />
          </span>
        </lv-button>
        <aw-select-version2
          v-if="isSortable"
          v-model:selected-options="sortSelectModel"
          :options="sortSelectOptions"
          option-id-property="key"
          option-label-property="name"
          :label="$awt('aw.common.product_list.filter.order_by')"
          :class="$style.sortSelectWrapper"
          :multiple="false"
          :input-size="screenRange['tablet-min'] ? 'lg-fat' : 'sm'"
          :dropdown-icon-attrs="{
            name: hasSwitcherIcon ? 'switcher-up-down-16' : 'arrow-down-16',
            size: 16,
            inheritColor: true
          }"
          @update:selected-options="sortData"
        />
      </client-only>
    </div>

    <client-only>
      <!-- region FILTER MODAL -->
      <aw-modal
        :opened="modal.type === modalType"
        size="md"
        styling="filter"
        :modal-title="$awt('aw.common.product_list.filter_row.filter')"
        @close-modal="closeFilterModal"
      >
        <template #content>
          <div>
            <aw-product-list-active-filters :class="$style.activeFilters" />
            <lv-heading
              tag="h3"
              level="3"
              version2
              :class="$style.filterSectionTitle"
            >
              {{ $awt('aw.product_list.filter.filter.title') }}
            </lv-heading>
            <aw-product-list-filters
              v-if="filters.length"
              :key="`filter-key-${modal.type === modalType}`"
              use-in-modal
              @activate-filter="activateFilter"
              @close-modal="closeFilterModal"
            />
          </div>
        </template>
      </aw-modal>
      <!-- endregion FILTER MODAL -->
    </client-only>
  </div>
</template>

<script>
  import { mapState } from 'pinia';
  import { defineAsyncComponent } from 'vue';
  import { LvButton, LvHeading, LvIcon } from '~~/common/components/loginet-vue-shop/index.mjs';
  import AwModal from '~~/common/components/Common/AwModal';
  import { uuid4 } from '~~/common/utils';
  import { M_FILTER } from '~~/common/config/Modal.js';

  import { useModalStore } from '~~/common/stores/modal';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useProductsStore } from '~~/common/stores/products';

  export default {
    name: 'AwProductListOrderByAndMobileFilterModal',
    components: {
      LvHeading,
      AwProductListActiveFilters: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwProductListActiveFilters')),
      LvIcon,
      LvButton,
      AwModal,
      AwProductListFilters: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwProductListFilters')),
      AwSelectVersion2: defineAsyncComponent(() => import('~~/common/components/Common/AwSelectVersion2')),
    },
    props: {
      isFilterable: {
        type: Boolean,
        default: true,
      },
      isSortable: {
        type: Boolean,
        default: true,
      },
      hasSwitcherIcon: {
        type: Boolean,
        default: false,
      },
      controlsVisibleStatus: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        modalType: null,
        sortSelectModel: this.getFilterInitialValue(),
        observer: null,
      };
    },
    computed: {
      ...mapState(useModalStore, {
        modal: state => state.activeGenericModal,
      }),
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      ...mapState(useProductsStore, {
        sortValues: state => state.sortValues,
        sortIdentifier: state => (state.sortKey || state.sortBy) ? `${state.sortKey}-${state.sortBy}` : '',
        filters: state => state.filters || [],
        filterBy: state => state.filterBy,
      }),
      filterCount () {
        return Object.keys(this.filterBy).filter(key => this.filterBy[key].length).length;
      },
      sortSelectOptions () {
        // TODO: set active ordering element
        const calculatedArray = [
          this.getFilterInitialValue(),
          ...((this.sortValues || []).flatMap((sortVal) => {
            return (sortVal.directions.length === 2
              ? ['asc', 'desc'].map(dir => ({
                value: {
                  sortKey: sortVal.value,
                  sortBy: dir,
                },
                name: `${sortVal.name} ${this.$awt('aw.common.product_list.filter_row.' + dir)}`,
              }))
              : [{
                value: {
                  sortKey: sortVal.value,
                  sortBy: sortVal.directions[0].toLowerCase(),
                },
                name: sortVal.name,
              }]
            );
          })),
        ];
        return calculatedArray.map(current => ({
          ...current,
          key: `${current.value.sortKey}-${current.value.sortBy}`,
        }));
      },
    },
    watch: {
      sortSelectOptions (newVal) {
        this.initDropdowns(newVal);
      },
    },
    beforeMount () {
      this.modalType = M_FILTER + uuid4();
    },
    mounted () {
      if (this.sortValues) {
        this.initDropdowns(this.sortSelectOptions);
      }
      if (this.controlsVisibleStatus) {
        this.addIntersectionObserver();
      }
    },
    beforeUnmount () {
      this.removeObserver();
    },
    methods: {
      getFilterInitialValue () {
        return {
          value: {
            sortBy: null,
            sortKey: null,
          },
          name: this.$awt('aw.common.product_list.filter_row.default'),
          key: 'null-null',
        };
      },
      addIntersectionObserver () {
        const element = this.$refs.filterModal;
        const callback = (entries) => {
          const isIntersecting = entries?.[0]?.isIntersecting;
          const productsStore = useProductsStore();
          productsStore.filterPanelVisible = !isIntersecting;
        };
        this.observer = new IntersectionObserver(callback, {
          root: null,
          rootMargin: '0px',
          threshold: 1.0,
        });
        this.observer.observe(element);
      },
      removeObserver () {
        this.observer?.disconnect();
      },
      activateFilter () {
        this.$awEvents.triggerEvent('activate-filter');
      },
      sortData (event) {
        this.$awEvents.triggerEvent('sort-data', event);
      },
      openFilterModal () {
        const modalStore = useModalStore();
        modalStore.showFilterModal({ modalType: this.modalType });
      },
      closeFilterModal () {
        const modalStore = useModalStore();
        modalStore.hideFilterModal({ modalType: this.modalType });
      },
      initDropdowns (drop) {
        if (this.sortIdentifier) {
          const newSortValue = drop.find(({ key }) => key === this.sortIdentifier);
          if (newSortValue && this.sortSelectModel !== newSortValue) {
            this.sortSelectModel = newSortValue;
          }
        }
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">

.sortSelectWrapper {
  position: relative;
  z-index: 10000;

  @include mobile(max) {
    width: calc(100vw - 140px);
  }

  [data-form-input-select] {
    font-weight: $font-weight-medium-v2 !important;
    justify-content: center !important;
    padding: 7px 19px !important;
    text-overflow: ellipsis;
    border-color: $color-olive-750;
    border-radius: 8px !important;
    gap: 8px;

    [data-form-input-select-title] {
      justify-content: flex-start !important;
    }

    [data-form-input-select-chevron] {
      margin-left: unset !important;
    }
  }
}

.activeFilters {
  margin-bottom: 20px;
}

.filterSectionTitle {
  font-size: 22px;
  font-weight: $font-weight-bold-v2;
  line-height: 28px;
  margin-bottom: 20px !important;
}

.filterModal {
  &Controls {
    display: grid;
    grid-template-columns: 100px auto;
    gap: 8px;
    @include tablet(min) {
      grid-template-columns: 100px 221px;
      justify-content: flex-start;
    }

    &Single {
      grid-template-columns: 1fr;
    }

    &ButtonContent {
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: center;
      white-space: nowrap;
      gap: 8px;
    }
  }
}
</style>
